import { cn } from "@util/tailwind";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    QuoteIcon,
    SparklesIcon,
} from "lucide-react";

type Category = "Brilliant" | "Excellent" | "Good" | "Inaccuracy" | "Mistake";

interface ExpandableFeedbackPointProps {
    category: Category;
    text: string;
    transcriptText?: string;
    explanation?: string;
    isExpanded?: boolean;
    onToggle?: () => void;
}

const dotColors: Record<Category, string> = {
    Brilliant: "bg-purple-600",
    Excellent: "bg-green-600",
    Good: "bg-emerald-600",
    Inaccuracy: "bg-amber-500",
    Mistake: "bg-red-600",
};

const accentBarColors: Record<Category, string> = {
    Brilliant: "bg-purple-500",
    Excellent: "bg-green-500",
    Good: "bg-emerald-500",
    Inaccuracy: "bg-amber-500",
    Mistake: "bg-red-500",
};

// Caption + icon color stays neutral gray across all categories — the vertical
// accent bar on the left is the only category-tinted cue inside the card.
const CAPTION_CLASS = "text-gray-500";
const CAPTION_ICON_CLASS = "text-gray-400";

export default function ExpandableFeedbackPoint({
    category,
    text,
    transcriptText,
    explanation,
    isExpanded = false,
    onToggle,
}: ExpandableFeedbackPointProps) {
    return (
        <div className="border-b border-gray-100 last:border-0">
            {/* Header row */}
            <button
                type="button"
                onClick={onToggle}
                className={cn(
                    "w-full text-left py-4 px-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50 transition-colors",
                    isExpanded && "bg-gray-50"
                )}
                aria-expanded={isExpanded}
            >
                <span
                    className={cn(
                        "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                        dotColors[category]
                    )}
                />
                <span className="flex-grow text-sm font-medium text-gray-900">
                    {text}
                </span>
                <span className="flex-shrink-0">
                    {isExpanded ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    )}
                </span>
            </button>

            {/* Expanded body */}
            {isExpanded && (transcriptText || explanation) && (
                <div className="px-4 pb-5 mt-4">
                    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                        <div className="flex">
                            {/* Vertical accent bar — only category cue in the expanded body */}
                            <div
                                className={cn("w-1 flex-shrink-0", accentBarColors[category])}
                                aria-hidden
                            />
                            <div className="flex-1 p-4 space-y-4">
                                {/* Quote block */}
                                {transcriptText && (
                                    <div>
                                        <div
                                            className={cn(
                                                "flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider font-semibold",
                                                CAPTION_CLASS
                                            )}
                                        >
                                            <QuoteIcon className={cn("w-3 h-3", CAPTION_ICON_CLASS)} />
                                            Quoted from your answer
                                        </div>
                                        <div className="rounded-md bg-neutral-50 border border-neutral-200 px-4 py-3">
                                            <p className="text-sm italic text-gray-700 leading-relaxed">
                                                “{transcriptText}”
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Explanation block */}
                                {explanation && (
                                    <div>
                                        <div
                                            className={cn(
                                                "flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider font-semibold",
                                                CAPTION_CLASS
                                            )}
                                        >
                                            <SparklesIcon className={cn("w-3 h-3", CAPTION_ICON_CLASS)} />
                                            Why this matters
                                        </div>
                                        <p className="text-sm text-gray-800 leading-relaxed">
                                            {explanation}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
